import * as THREE from 'three';

/**
 * Normalizes a URL by stripping query parameters (signatures).
 * This allows caching signed URLs under a stable key.
 */
export function normalizeUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    urlObj.search = ''; // Remove all query params (signatures)
    return urlObj.toString();
  } catch {
    // If URL parsing fails, return as-is
    return url;
  }
}

/**
 * Creates a LoadingManager configured to handle signed URL caching.
 * Assets loaded through this manager will be cached under their normalized URL
 * (without query params), so subsequent loads with different signatures hit the cache.
 */
export function createSignedUrlLoadingManager(): THREE.LoadingManager {
  // Enable Three.js cache (disabled by default)
  THREE.Cache.enabled = true;

  const manager = new THREE.LoadingManager();
  const urlToNormalizedMap = new Map<string, string>();

  // Set up URL modifier to handle signed URL caching
  manager.setURLModifier((url) => {
    const normalized = normalizeUrl(url);

    // Check if we have cached data for this normalized URL
    const cached = THREE.Cache.get(normalized);
    if (cached) {
      // Store under the actual URL so Three.js finds it
      THREE.Cache.add(url, cached);
    }

    // Track mapping for post-load caching
    urlToNormalizedMap.set(url, normalized);

    return url; // Return original URL for actual fetch
  });

  // After load completes, also cache under normalized keys
  manager.onLoad = () => {
    urlToNormalizedMap.forEach((normalized, original) => {
      const data = THREE.Cache.get(original);
      if (data) {
        THREE.Cache.add(normalized, data);
      }
    });
  };

  return manager;
}
